import { useEffect, useState, useMemo } from 'react';
import {
  Box, Stack, Group, Title, Text, Button, Paper, Image, Loader, Alert, Badge,
} from '@mantine/core';
import { Link } from 'react-router-dom';
import { casosApi } from '../../casos-api/casosApi';
import { LogosEvento } from '../Kiosk/LogosEvento';
import { CASOS_CON_IPG } from '../../utils/cvdl';
import './casos.css';
import '../Kiosk/cvdlClaro.css';

/**
 * Landing de Caso Abierto: la lista de casos publicados.
 *
 * Es a donde vuelve quien terminó uno ("VER OTRO CASO") y a donde cae quien
 * pide un caso que no existe. Hasta ahora esos dos botones apuntaban a una
 * ruta que no estaba declarada, así que llevaban a la página de 404 del sitio.
 *
 * El orden lo decide el backend —más nuevo primero, por publishing.webAt— y no
 * se toca acá: si mañana se destaca un caso, se resuelve en la consulta y no
 * en la vista.
 */
export function CasosIndex() {
  const [casos, setCasos] = useState(null);
  const [error, setError] = useState(null);
  const [area, setArea] = useState(null);

  useEffect(() => {
    let vivo = true;
    casosApi.listar()
      .then(r => { if (vivo) setCasos(r.casos || []); })
      .catch(e => { if (vivo) setError(e.message); });
    return () => { vivo = false; };
  }, []);

  // Las áreas salen de los casos que hay, no de la lista completa de
  // especialidades: un filtro con doce opciones y cinco casos es ruido.
  const areas = useMemo(() => {
    const vistas = [...new Set((casos || []).map(c => c.specialty).filter(Boolean))];
    return vistas.sort((a, b) => etiquetaArea(a).localeCompare(etiquetaArea(b)));
  }, [casos]);

  const visibles = area ? casos.filter(c => c.specialty === area) : casos;

  return (
    <Box className="cvdl-claro caso-marco">
      <Box className="caso-ancho">
        <LogosEvento h={26} conIpg={CASOS_CON_IPG} c="var(--mantine-color-forvet-8)" mb="xl" />

        <Stack gap="xs" mb="xl">
          <Title order={1} className="caso-titulo">Caso Abierto</Title>
          <Text size="lg" c="dimmed">
            Casos clínicos reales, aportados por docentes y por la comunidad.
            Vos decidís, y después comparás con lo que hizo quien lo atendió.
          </Text>
          <Text fz="var(--fs-small)" c="dimmed">
            Sin registro. Unos 90 segundos cada uno.
          </Text>
        </Stack>

        {error && <Alert color="red" radius="md">{error}</Alert>}
        {!casos && !error && <Loader />}

        {casos && !casos.length && (
          <Alert color="gray" radius="md">
            Todavía no hay casos publicados. Los primeros corren en la comunidad
            de WhatsApp y llegan a la web después.
          </Alert>
        )}

        {areas.length > 1 && (
          <Group gap="xs" mb="lg" className="caso-filtros">
            <FiltroArea activo={!area} onClick={() => setArea(null)}>
              Todos ({casos.length})
            </FiltroArea>
            {areas.map(a => (
              <FiltroArea key={a} activo={area === a} onClick={() => setArea(a)}>
                {etiquetaArea(a)}
              </FiltroArea>
            ))}
          </Group>
        )}

        <Stack gap="md">
          {(visibles || []).map(c => <TarjetaCaso key={c.slug} caso={c} />)}
        </Stack>
      </Box>
    </Box>
  );
}

function FiltroArea({ activo, onClick, children }) {
  return (
    <Button
      size="xs" radius="xl"
      variant={activo ? 'filled' : 'outline'}
      onClick={onClick}
    >
      {children}
    </Button>
  );
}

function TarjetaCaso({ caso }) {
  const p = caso.paciente || {};
  const ficha = [p.species, p.breed, p.sex, p.age].filter(Boolean).join(' · ');

  return (
    <Paper
      withBorder radius="md" p={0} className="caso-tarjeta"
      component={Link} to={`/casos/${caso.slug}`}
    >
      <Group gap={0} wrap="nowrap" align="stretch">
        {p.photoUrl && (
          <Box className="caso-tarjeta-foto">
            {/* Decorativa: todo lo que importa está en el texto de al lado,
                así que no repetimos la descripción en el alt. */}
            <Image src={p.photoUrl} alt="" h="100%" w="100%" fit="cover" />
          </Box>
        )}

        <Stack gap={6} p="md" style={{ flex: 1, minWidth: 0 }}>
          <Group gap="xs" wrap="wrap">
            <Badge >{etiquetaArea(caso.specialty)}</Badge>
            {caso.resueltos >= 20 && (
              <Text fz="var(--fs-small)" c="dimmed">
                {caso.resueltos} colegas lo resolvieron
              </Text>
            )}
          </Group>

          <Text fw={700} fz="lg" c="forvet.8" className="caso-tarjeta-titulo">
            {caso.title}
          </Text>

          {caso.resena && (
            <Text fz="var(--fs-small)" c="dimmed" className="caso-tarjeta-resena">
              {caso.resena}
            </Text>
          )}

          <Text fz="var(--fs-small)" c="dimmed">
            {ficha}
            {caso.autor?.nombre && ` · aportado por ${caso.autor.nombre}`}
          </Text>
        </Stack>
      </Group>
    </Paper>
  );
}

const AREAS = {
  MEDICINA_FELINA: 'Medicina felina', MEDICINA_INTERNA: 'Medicina interna',
  CIRUGIA: 'Cirugía', IMAGENES: 'Imágenes', CARDIOLOGIA: 'Cardiología',
  DERMATOLOGIA: 'Dermatología', ONCOLOGIA: 'Oncología', EMERGENCIAS: 'Emergencias',
  ANESTESIOLOGIA: 'Anestesiología', NUTRICION: 'Nutrición', FISIOTERAPIA: 'Fisioterapia',
  NEUROLOGIA: 'Neurología', EXOTICOS: 'Exóticos', GRANDES_ANIMALES: 'Grandes animales',
  GESTION: 'Gestión de la clínica', OTRA: 'Otra',
};
const etiquetaArea = a => AREAS[a] || a;
